/**
 * Minimal markdown renderer for Insight replies.
 *
 * The model answers in markdown — bold for the finding that matters, bullets
 * for differentials, numbers for steps. The panel was rendering it as raw text,
 * so a clinician read `**Consider ACS**` and `- chest pain` literally.
 *
 * Deliberately hand-rolled and deliberately small:
 *  - It returns React ELEMENTS, never HTML. There is no dangerouslySetInnerHTML
 *    anywhere in here, so model output cannot inject markup into the console.
 *  - It covers only what the model actually emits. Anything it does not
 *    understand falls through as plain text rather than disappearing.
 */

import React from 'react';

const CODE_STYLE = {
  background: 'rgba(255,255,255,0.09)',
  borderRadius: '4px',
  padding: '1px 5px',
  fontSize: '0.92em',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
};

/**
 * Inline spans: **bold**, *italic*, `code`.
 * Single pass, longest-delimiter-first, so `**a**` never matches as italic.
 */
function renderInline(text, keyPrefix = 'i') {
  const out = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|(?<![*\w])\*(?!\s)[^*]+(?<!\s)\*(?!\*))/g;
  let last = 0;
  let m;
  let n = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyPrefix}-${n++}`;
    if (tok.startsWith('**')) {
      out.push(<strong key={key} style={{ color: '#fff', fontWeight: 700 }}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(<code key={key} style={CODE_STYLE}>{tok.slice(1, -1)}</code>);
    } else {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : [text];
}

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const HEADING = /^\s*(#{1,4})\s+(.*)$/;

/** Group consecutive list lines so a list renders as one block. */
function blocksOf(lines) {
  const blocks = [];
  let buf = null;

  const flush = () => { if (buf) { blocks.push(buf); buf = null; } };

  for (const line of lines) {
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    const numbered = NUMBERED.exec(line);

    if (heading) { flush(); blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] }); continue; }
    if (bullet) {
      if (buf?.type !== 'ul') { flush(); buf = { type: 'ul', items: [] }; }
      buf.items.push(bullet[1]);
      continue;
    }
    if (numbered) {
      if (buf?.type !== 'ol') { flush(); buf = { type: 'ol', items: [], start: Number(numbered[1]) }; }
      buf.items.push(numbered[2]);
      continue;
    }
    if (!line.trim()) { flush(); continue; }
    if (buf?.type === 'p') buf.lines.push(line);
    else { flush(); buf = { type: 'p', lines: [line] }; }
  }
  flush();
  return blocks;
}

export default function InsightMarkdown({ text }) {
  if (!text) return null;
  const blocks = blocksOf(String(text).split('\n'));

  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'heading') {
          return (
            <div key={i} style={{
              fontSize: b.level <= 2 ? '13.5px' : '12.5px',
              fontWeight: 700, color: '#fff',
              margin: i === 0 ? '0 0 5px' : '11px 0 5px'
            }}>
              {renderInline(b.text, `h${i}`)}
            </div>
          );
        }
        if (b.type === 'ul' || b.type === 'ol') {
          const ordered = b.type === 'ol';
          return (
            <ul key={i} style={{
              listStyle: 'none', margin: i === 0 ? '0 0 6px' : '6px 0',
              padding: 0, display: 'flex', flexDirection: 'column', gap: '4px'
            }}>
              {b.items.map((item, j) => (
                <li key={j} style={{ display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                  <span style={{
                    color: '#8b8bf5', fontSize: ordered ? '11.5px' : '13px',
                    flexShrink: 0, fontWeight: ordered ? 700 : 400,
                    minWidth: ordered ? '14px' : 'auto', lineHeight: 1.5
                  }}>
                    {ordered ? `${(b.start || 1) + j}.` : '•'}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>{renderInline(item, `l${i}-${j}`)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} style={{ margin: i === 0 ? '0' : '8px 0 0' }}>
            {renderInline(b.lines.join('\n'), `p${i}`)}
          </p>
        );
      })}
    </>
  );
}

export { renderInline, blocksOf };
